import { NavLink, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { apiFetch } from "@/lib/supabase";
import {
  LayoutDashboard, Users, Flower2, FileText, ClipboardList, Layers,
  Menu, X, LogOut, Hammer, Truck, RefreshCw, AlertTriangle, ShoppingCart, UserPlus, MessageCircle, Globe,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { useVelvet } from "@/context/VelvetContext";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ['admin','manager','sales','purchasing'], testid: "nav-dashboard" },
  { to: "/leads", label: "Leads", icon: UserPlus, roles: ['admin','manager','sales'], testid: "nav-leads" },
  { to: "/inbox", label: "Inbox · WhatsApp", icon: MessageCircle, roles: ['admin','manager','sales'], testid: "nav-inbox" },
  { to: "/squarespace", label: "Squarespace", icon: Globe, roles: ['admin','manager'], testid: "nav-squarespace" },
  { to: "/customers", label: "Customers", icon: Users, roles: ['admin','manager','sales'], testid: "nav-customers" },
  { to: "/products", label: "Products & Recipes", icon: Flower2, roles: ['admin','manager','designer','sales','purchasing'], testid: "nav-products" },
  { to: "/collections", label: "Collections", icon: Layers, roles: ['admin','manager','sales','purchasing'], testid: "nav-collections" },
  { to: "/quotations", label: "Quotations", icon: FileText, roles: ['admin','manager','sales'], testid: "nav-quotations" },
  { to: "/orders", label: "Orders & Production", icon: ClipboardList, roles: ['admin','manager','sales'], testid: "nav-orders" },
  { to: "/production", label: "My Production", icon: Hammer, roles: ['designer','admin','manager'], testid: "nav-production" },
  { to: "/purchasing", label: "Purchasing", icon: ShoppingCart, roles: ['admin','manager','purchasing'], testid: "nav-purchasing" },
  { to: "/my-deliveries", label: "My Deliveries", icon: Truck, roles: ['driver','admin','manager'], testid: "nav-my-deliveries" },
];

const ROLE_LABEL = {
  admin: 'Owner / Admin',
  manager: 'Manager / Dispatcher',
  sales: 'Sales / POS',
  designer: 'Designer',
  driver: 'Driver',
  purchasing: 'Purchasing / Nexa',
};

function SidebarInner({ onNavigate }) {
  const { profile, signOut } = useAuth();
  const items = NAV.filter((n) => n.roles.includes(profile?.role));
  // Lightweight attention count for the Inbox nav item (independent of the Inbox page).
  const [attention, setAttention] = useState(0);
  const inflight = useRef(false);
  useEffect(() => {
    if (!['admin','manager','sales'].includes(profile?.role)) return;
    let timer = null;
    const tick = async () => {
      if (document.hidden || inflight.current) return;
      inflight.current = true;
      try {
        const s = await apiFetch('/whatsapp/status');
        setAttention(Number(s?.attention_count || 0));
      } catch { /* silent */ } finally { inflight.current = false; }
    };
    tick();
    timer = setInterval(tick, 10000);
    const onVis = () => { if (!document.hidden) tick(); };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', onVis);
    return () => { clearInterval(timer); document.removeEventListener('visibilitychange', onVis); window.removeEventListener('focus', onVis); };
  }, [profile?.role]);
  return (
    <aside className="h-full w-64 shrink-0 bg-[hsl(var(--soft-blush))]/60 border-r border-border flex flex-col" data-testid="app-sidebar">
      <div className="px-6 pt-8 pb-6 border-b border-border">
        <div className="velvet-eyebrow text-foreground/60">Velvet Bloom Flowers</div>
        <div className="font-serif text-3xl leading-none tracking-tight text-foreground mt-1">
          Velvet <span className="italic text-[hsl(var(--muted-green-dark))]">OS</span>
        </div>
      </div>
      <nav className="flex-1 px-3 py-6 space-y-1">
        {items.map((item) => (
          <NavLink key={item.to} to={item.to} onClick={onNavigate} data-testid={item.testid}
            className={({ isActive }) => cn(
              "group flex items-center gap-3 px-3 py-2.5 text-sm font-medium border transition-colors duration-200",
              isActive ? "bg-card border-border text-foreground"
                       : "border-transparent text-muted-foreground hover:bg-card/60 hover:text-foreground"
            )}>
            <item.icon className="h-4 w-4" strokeWidth={1.75} />
            <span className="flex-1">{item.label}</span>
            {item.testid === 'nav-inbox' && attention > 0 && (
              <span className="ml-auto bg-[hsl(0_75%_45%)] text-background text-[10px] font-medium min-w-[20px] h-[18px] rounded-full flex items-center justify-center px-1.5"
                    data-testid="nav-inbox-attention">
                {attention}
              </span>
            )}
          </NavLink>
        ))}
      </nav>
      <div className="px-4 py-4 border-t border-border space-y-3">
        <div className="px-2">
          <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground/70">Signed in as</div>
          <div className="text-sm font-medium mt-0.5 truncate" data-testid="current-user">{profile?.full_name || profile?.email}</div>
          <div className="text-xs text-muted-foreground">{ROLE_LABEL[profile?.role] || '—'}</div>
        </div>
        <button onClick={signOut} className="w-full inline-flex items-center gap-2 justify-center px-3 py-2 text-sm border border-border bg-card hover:bg-[hsl(var(--muted))] transition-colors" data-testid="sign-out-btn">
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </div>
    </aside>
  );
}

export default function AppShell({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { profile } = useAuth();
  const { isRetrying, hasErrors, loadErrors, retryingKeys,
          reloadOrders, reloadDeliveries, reloadCustomers, reloadProducts, reloadQuotations, reloadStaff } = useVelvet();
  const items = NAV.filter((n) => n.roles.includes(profile?.role));
  const currentLabel = items.find((n) => location.pathname.startsWith(n.to))?.label || "Velvet OS";
  const retryAll = () => {
    reloadOrders(); reloadDeliveries(); reloadCustomers();
    reloadProducts(); reloadQuotations(); reloadStaff();
  };
  return (
    <div className="min-h-screen flex bg-background">
      <div className="hidden lg:block sticky top-0 h-screen">
        <SidebarInner />
      </div>
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-foreground/40" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-overlay">
          <div className="absolute inset-y-0 left-0 h-full" onClick={(e) => e.stopPropagation()}>
            <SidebarInner onNavigate={() => setMobileOpen(false)} />
          </div>
        </div>
      )}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-30 h-16 bg-background/80 backdrop-blur border-b border-border flex items-center gap-4 px-4 lg:px-8" data-testid="app-topbar">
          <button className="lg:hidden -ml-2 p-2 border border-border bg-card" onClick={() => setMobileOpen(true)} aria-label="Open navigation" data-testid="mobile-nav-open">
            <Menu className="h-5 w-5" />
          </button>
          <div className="flex items-baseline gap-3 min-w-0">
            <span className="velvet-eyebrow hidden sm:inline">Velvet OS</span>
            <span className="hidden sm:inline text-muted-foreground/60">/</span>
            <span className="font-serif text-xl tracking-tight truncate">{currentLabel}</span>
          </div>
          <div className="ml-auto flex items-center gap-3">
            {isRetrying ? (
              <span
                className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 border border-[hsl(60_20%_78%)] bg-[hsl(60_30%_92%)] text-foreground"
                title={`Retrying: ${[...retryingKeys].join(', ')}`}
                data-testid="reconnecting-indicator"
              >
                <RefreshCw className="h-3 w-3 animate-spin" />
                Reconnecting…
              </span>
            ) : hasErrors ? (
              <button
                onClick={retryAll}
                className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 border border-destructive/50 bg-[hsl(var(--soft-blush))]/60 text-foreground hover:bg-[hsl(var(--soft-blush))]"
                title={`Failed to load: ${Object.keys(loadErrors).join(', ')}. Click to retry.`}
                data-testid="reconnect-error-indicator"
              >
                <AlertTriangle className="h-3 w-3" />
                Retry {Object.keys(loadErrors).join(', ')}
              </button>
            ) : null}
            <span className="hidden md:inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-[hsl(var(--muted-green))]" /> Live
            </span>
            <div className="h-8 w-8 rounded-full bg-[hsl(var(--soft-blush-dark))] border border-border flex items-center justify-center font-serif text-sm text-foreground uppercase" data-testid="user-avatar">
              {(profile?.full_name || profile?.email || 'V')[0]}
            </div>
          </div>
          {mobileOpen && (
            <button className="lg:hidden absolute right-4 top-4 p-2" onClick={() => setMobileOpen(false)} aria-label="Close navigation">
              <X className="h-5 w-5" />
            </button>
          )}
        </header>
        <main className="flex-1 min-w-0">{children}</main>
      </div>
    </div>
  );
}
